/**
 * Coada de mutatii offline — „un VPN cazut nu costa o dimineata de munca".
 *
 * Situatia reala: se lucreaza de acasa, prin VPN, iar VPN-ul pica. Fara coada, fiecare celula
 * editata in urmatoarele minute e o eroare rosie si o valoare pierduta, iar omul afla abia cand se
 * uita la total. Cu coada, mutatiile se PAUZEAZA (TanStack Query face asta singur cand
 * `onlineManager` spune ca nu e retea), se scriu in IndexedDB si se reiau in ordine cand reteaua
 * revine — inclusiv dupa un refresh sau un restart de browser.
 *
 * Trei decizii care nu sunt evidente:
 *
 *   1. **Se persista DOAR mutatiile, nu si interogarile.** Cifrele de trezorerie nu au voie sa fie
 *      restaurate din IndexedDB si afisate ca si cum ar fi proaspete: un `Disponibil` de ieri, fara
 *      ora langa el, e mai periculos decat un ecran gol. Deci `shouldDehydrateQuery` intoarce mereu
 *      `false`.
 *   2. **`mutationFn` nu se serializeaza.** Dupa reload, o mutatie restaurata are doar cheia si
 *      variabilele; functia se regaseste prin `setMutationDefaults`, de aceea cheile de mutatie din
 *      `query-keys.ts` sunt un contract pe disc si nu se redenumesc usor.
 *   3. **Nu tot se pune in coada.** Incarcarile de fisiere (un `File` nu supravietuieste
 *      serializarii) si orice atinge un secret bancar au `networkMode: 'always'`: esueaza pe loc,
 *      vizibil, in loc sa fie reluate automat ore mai tarziu, fara om in fata ecranului.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { onlineManager, useMutationState, type QueryClient } from '@tanstack/react-query';
import {
  persistQueryClient,
  type PersistedClient,
  type Persister,
} from '@tanstack/query-persist-client-core';

import { chei } from '../lib/query-keys.ts';
import { fnAmanare, fnComutaAmanareAuto } from './useAmanare.ts';
import { fnStergeAtasament } from './useAtasamente.ts';
import { fnPutPozitieCash } from './useCashflow.ts';
import {
  fnPatchComentariu,
  fnPostComentariu,
  fnRezolvaComentariu,
} from './useComentarii.ts';
import { fnSupersedeTarif } from './useComisioane.ts';
import {
  fnCotatiiBulk,
  fnPatchCotatie,
  fnPreiaBnr,
  fnSetCastigator,
} from './useValutar.ts';
import { fnActiuniBloc, fnCreeazaRunda, fnPatchIntrare } from './useZi.ts';

// ---------------------------------------------------------------------------
// Stocarea
// ---------------------------------------------------------------------------

/**
 * Interfata minima de stocare.
 *
 * Exista ca sa se poata injecta un magazin fals in teste: `indexedDB` nu are ce sa caute intr-un
 * mediu de test Node, dar comportamentul cozii (persistare, restaurare dupa „reload", reluare) e
 * exact ce trebuie testat.
 */
export interface MagazinPersistent {
  citeste: () => Promise<PersistedClient | undefined>;
  scrie: (valoare: PersistedClient) => Promise<void>;
  sterge: () => Promise<void>;
}

export const NUME_BAZA = 'evomag-trezorerie';
export const NUME_STORE = 'coada-mutatii';
export const CHEIE_COADA = 'client';

function deschide(numeBaza: string, numeStore: string): Promise<IDBDatabase> {
  return new Promise((rezolva, respinge) => {
    const cerere = indexedDB.open(numeBaza, 1);
    cerere.onupgradeneeded = () => {
      const baza = cerere.result;
      if (!baza.objectStoreNames.contains(numeStore)) baza.createObjectStore(numeStore);
    };
    cerere.onsuccess = () => rezolva(cerere.result);
    cerere.onerror = () => respinge(cerere.error ?? new Error('IndexedDB indisponibil'));
  });
}

function tranzactie<T>(
  baza: IDBDatabase,
  numeStore: string,
  mod: IDBTransactionMode,
  operatie: (magazin: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((rezolva, respinge) => {
    const tx = baza.transaction(numeStore, mod);
    const cerere = operatie(tx.objectStore(numeStore));
    cerere.onsuccess = () => rezolva(cerere.result);
    cerere.onerror = () => respinge(cerere.error ?? new Error('Scriere IndexedDB esuata'));
  });
}

/**
 * Magazin real, peste IndexedDB.
 *
 * NU `localStorage`: e sincron (blocheaza firul principal exact cand se tasteaza intr-o celula),
 * are ~5 MB si stocheaza doar string-uri. IndexedDB e asincron si structurat, iar o coada de 200 de
 * mutatii incape fara sa se apropie de vreo limita.
 */
export function magazinIndexedDb(optiuni?: {
  numeBaza?: string;
  numeStore?: string;
  cheie?: string;
}): MagazinPersistent {
  const numeBaza = optiuni?.numeBaza ?? NUME_BAZA;
  const numeStore = optiuni?.numeStore ?? NUME_STORE;
  const cheie = optiuni?.cheie ?? CHEIE_COADA;

  return {
    citeste: async () => {
      const baza = await deschide(numeBaza, numeStore);
      try {
        return await tranzactie<PersistedClient | undefined>(baza, numeStore, 'readonly', (s) =>
          s.get(cheie),
        );
      } finally {
        baza.close();
      }
    },
    scrie: async (valoare) => {
      const baza = await deschide(numeBaza, numeStore);
      try {
        await tranzactie(baza, numeStore, 'readwrite', (s) => s.put(valoare, cheie));
      } finally {
        baza.close();
      }
    },
    sterge: async () => {
      const baza = await deschide(numeBaza, numeStore);
      try {
        await tranzactie(baza, numeStore, 'readwrite', (s) => s.delete(cheie));
      } finally {
        baza.close();
      }
    },
  };
}

/** Adaptorul catre contractul `Persister` din `@tanstack/query-persist-client-core`. */
export function creeazaPersister(magazin: MagazinPersistent): Persister {
  return {
    persistClient: (client) => magazin.scrie(client),
    restoreClient: () => magazin.citeste(),
    removeClient: () => magazin.sterge(),
  };
}

// ---------------------------------------------------------------------------
// Inregistrarea functiilor de mutatie
// ---------------------------------------------------------------------------

type FunctieMutatie = (variabile: never) => Promise<unknown>;

/**
 * Ce se REIA dupa reconectare sau dupa reload.
 *
 * Lipsesc deliberat: incarcarea de atasamente (`File` neserializabil) si tot ce tine de credentiale
 * (o parola nu are ce sa caute in IndexedDB, iar o dezvaluire executata automat ar fi o intrare in
 * jurnalul de audit fara nimeni in fata ecranului).
 */
export const MUTATII_REJUCABILE: ReadonlyArray<readonly [readonly unknown[], FunctieMutatie]> = [
  [chei.mutatii.patchIntrare(), fnPatchIntrare as FunctieMutatie],
  [chei.mutatii.creeazaRunda(), fnCreeazaRunda as FunctieMutatie],
  [chei.mutatii.actiuniBloc(), fnActiuniBloc as FunctieMutatie],
  [chei.mutatii.amanare(), fnAmanare as FunctieMutatie],
  [chei.mutatii.amanareAuto(), fnComutaAmanareAuto as FunctieMutatie],
  [chei.mutatii.pozitieCash(), fnPutPozitieCash as FunctieMutatie],
  [chei.mutatii.patchCotatie(), fnPatchCotatie as FunctieMutatie],
  [chei.mutatii.cotatiiBulk(), fnCotatiiBulk as FunctieMutatie],
  [chei.mutatii.preiaBnr(), fnPreiaBnr as FunctieMutatie],
  [chei.mutatii.setCastigator(), fnSetCastigator as FunctieMutatie],
  [chei.mutatii.postComentariu(), fnPostComentariu as FunctieMutatie],
  [chei.mutatii.patchComentariu(), fnPatchComentariu as FunctieMutatie],
  [chei.mutatii.rezolvaComentariu(), fnRezolvaComentariu as FunctieMutatie],
  [chei.mutatii.stergeAtasament(), fnStergeAtasament as FunctieMutatie],
  [chei.mutatii.supersedeTarif(), fnSupersedeTarif as FunctieMutatie],
];

/**
 * Leaga fiecare cheie de mutatie de functia ei.
 *
 * Trebuie apelat INAINTE de restaurare: o mutatie hidratata isi cauta `mutationFn` in defaults chiar
 * in momentul construirii, iar una fara functie ramane pentru totdeauna in coada, „in asteptare",
 * fara sa se plânga.
 */
export function inregistreazaMutatii(queryClient: QueryClient): void {
  for (const [cheie, fn] of MUTATII_REJUCABILE) {
    queryClient.setMutationDefaults(cheie, {
      mutationFn: fn as (variabile: unknown) => Promise<unknown>,
    });
  }
}

// ---------------------------------------------------------------------------
// Pornirea cozii
// ---------------------------------------------------------------------------

export interface OptiuniCoada {
  queryClient: QueryClient;
  magazin?: MagazinPersistent;
  /**
   * Se schimba cand forma variabilelor de mutatie devine incompatibila. O coada mai veche decat
   * `buster` se arunca, in loc sa fie rejucata cu un corp pe care serverul nu il mai intelege.
   */
  buster?: string;
  /** Peste 48 h o coada nu mai are sens: ziua de plati la care se refera a trecut. */
  maxAge?: number;
}

export interface ControlCoada {
  /** Se rezolva dupa restaurare — util in teste si la afisarea primului ecran. */
  gata: Promise<void>;
  opreste: () => void;
}

export const BUSTER_IMPLICIT = 'v1';
export const VECHIME_MAXIMA_MS = 48 * 60 * 60 * 1000;

export function pornesteCoadaOffline(optiuni: OptiuniCoada): ControlCoada {
  const { queryClient } = optiuni;
  const persister = creeazaPersister(optiuni.magazin ?? magazinIndexedDb());

  inregistreazaMutatii(queryClient);

  const [dezaboneazaPersistenta, restaurare] = persistQueryClient({
    queryClient,
    persister,
    buster: optiuni.buster ?? BUSTER_IMPLICIT,
    maxAge: optiuni.maxAge ?? VECHIME_MAXIMA_MS,
    dehydrateOptions: {
      // Interogarile NU se persista: vezi comentariul din capul fisierului.
      shouldDehydrateQuery: () => false,
      shouldDehydrateMutation: (mutatie) => mutatie.state.isPaused,
    },
  });

  const gata = restaurare.then(async () => {
    // Daca reteaua e deja acolo la pornire (reload dupa ce VPN-ul a revenit), coada pleaca imediat.
    if (onlineManager.isOnline()) {
      await queryClient.resumePausedMutations();
      // Randurile scrise cu intarziere pot fi vechi de ore: tot ce e pe ecran se reciteste.
      await queryClient.invalidateQueries({ queryKey: chei.tot() });
    }
  });

  const dezaboneazaRetea = onlineManager.subscribe((online) => {
    if (!online) return;
    void queryClient.resumePausedMutations().then(() =>
      queryClient.invalidateQueries({ queryKey: chei.tot() }),
    );
  });

  return {
    gata,
    opreste: () => {
      dezaboneazaPersistenta();
      dezaboneazaRetea();
    },
  };
}

// ---------------------------------------------------------------------------
// Bannerul de offline
// ---------------------------------------------------------------------------

export interface StareCoada {
  /** Mutatii pauzate: scrise local, care ASTEAPTA reteaua. */
  inAsteptare: number;
  /** Mutatii in zbor chiar acum. */
  inZbor: number;
  /** Mutatii care au esuat definitiv si asteapta o decizie umana. */
  esuate: number;
  esteOnline: boolean;
}

function abonareRetea(asculta: () => void): () => void {
  return onlineManager.subscribe(() => asculta());
}

function stareRetea(): boolean {
  return onlineManager.isOnline();
}

export function useEsteOnline(): boolean {
  // `true` pe server: un banner de „offline" randat static ar fi mereu gresit.
  return useSyncExternalStore(abonareRetea, stareRetea, () => true);
}

/**
 * Contorul pentru bannerul „N modificări în așteptare".
 *
 * Se numara din `MutationCache`, nu dintr-un contor propriu: cache-ul e singurul care stie si de
 * mutatiile restaurate din IndexedDB, pe care tabul curent nu le-a pornit niciodata.
 */
export function useCoadaMutatii(): StareCoada {
  const stari = useMutationState({
    select: (mutatie) => ({
      status: mutatie.state.status,
      isPaused: mutatie.state.isPaused,
    }),
  });
  const esteOnline = useEsteOnline();

  return {
    inAsteptare: stari.filter((s) => s.isPaused).length,
    inZbor: stari.filter((s) => s.status === 'pending' && !s.isPaused).length,
    esuate: stari.filter((s) => s.status === 'error').length,
    esteOnline,
  };
}

/** Butonul `Reia acum` din banner: forteaza golirea cozii fara sa astepte evenimentul de retea. */
export function useReiaCoada(queryClient: QueryClient): () => Promise<void> {
  return useCallback(async () => {
    await queryClient.resumePausedMutations();
    await queryClient.invalidateQueries({ queryKey: chei.tot() });
  }, [queryClient]);
}
